import React from 'react';
import Routes from 'src/modules/Navigator/Navigator.route';
import { useHistory } from 'react-router-dom';
import { ReactComponent as DocumentIcon } from 'src/assets/icons/document-icon.svg';
import PrimaryButton from 'src/components/PrimaryButton/PrimaryButton.component';
import { useCookies } from 'react-cookie';
import { t } from 'src/i18n';
import {
  SpanStyleBold,
  TableCellDocument,
  TableWrapper,
  useStyles
} from '../Dashboard.style';
import { TableBody, TableHead, TableRow } from '@material-ui/core';
import API from 'src/api/api';
import { APP_CONSTANT } from 'src/constants';
import { parseJSON } from 'src/utils/common.util';
import LoadingContainer from 'src/components/LoadingContainer/LoadingContainer';
import { pathOr } from 'ramda';

interface ISingleDocument {
  documents: {
    id: string;
    secureId: string;
    status: string;
    name: string;
    submissionId?: number;
    secureSubmissionId?: string;
  }[];
  totalResults: number;
}

interface ILegalGuardianDocument {
  documents: {
    id: string;
    secureId: string;
    status: string;
    name: string;
    submissionId?: number;
    secureSubmissionId?: string;
  }[];
  totalResults: number;
  hasMore: boolean;
  nameFirst: string;
  nameLast: string;
}

type Document = ILegalGuardianDocument | ISingleDocument;

interface IProps {
  cookies: {
    userInfo: {
      name: { nameFirst: string; nameLast: string };
    };
    role: string;
    token: string;
  };
}

const UserDocumentAttention: React.FC<IProps> = ({ cookies }) => {
  const history = useHistory();
  const [res, setRes] = React.useState<any>({});
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isError, setIsError] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [numberDocument, setNumberDocument] = React.useState<number>(0);
  const [dataUserDocumentation, setDataUserDocument] = React.useState<
    Document[]
  >([]);
  const role = cookies.role;
  const classes = useStyles();
  const [cookie, setCookie] = useCookies([]);
  const token = cookies.token;
  const goToPatientDocumentList = () => {
    history.push(Routes.PatientDocument);
  };

  const urlQuery = () => {
    if (!token) {
      return `/user/v1/public/guest/documents/?accountId=${
        (parseJSON('tenantInfo') as any).accountId
      }`;
    }

    return '/user/v1/documents';
  };

  React.useEffect(() => {
    setIsLoading(true);

    const documentRequests = [API().get(urlQuery())];
    if (cookies.role === APP_CONSTANT.ROLE_LEGAL_GUARDIAN) {
      documentRequests.push(API().get('user/v1/family/documents'));
    }

    Promise.all(documentRequests)
      .then((data) => {
        const [currentDocuments, familiyDocuments] = data;

        let allRawDocuments: any[] = [];

        if (currentDocuments && currentDocuments.data) {
          currentDocuments.data.nameFirst = pathOr('', ['name', 'nameFirst'])(
            cookie.userInfo
          );
          currentDocuments.data.nameLast = pathOr('', ['name', 'nameLast'])(
            cookie.userInfo
          );
          currentDocuments.data.contactId = pathOr('', ['id'])(cookie.userInfo);

          allRawDocuments = [(currentDocuments as any).data];
        }

        if (familiyDocuments && familiyDocuments.data) {
          allRawDocuments = [...allRawDocuments, ...familiyDocuments.data];
        }

        setRes(allRawDocuments);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        setIsError(true);
        setErrorMessage('documentAttentions.error');
      });
  }, [cookies.role]);

  React.useEffect(() => {
    if (!res.length) {
      return;
    }
    const documents = pathOr([], ['0', 'documents'])(res);
    let num = 0;

    if (!token) {
      if (sessionStorage.getItem('guestSubmission')) {
        try {
          num =
            documents.length -
            JSON.parse(sessionStorage.getItem('guestSubmission') || '[]')
              .length;
        } catch (error) {
          // TODO:handle error
        }
      } else {
        num = documents.length;
      }
    }

    if (role === APP_CONSTANT.ROLE_SINGLE_USER) {
      num = documents.filter((item) => (item as any).status === 'pending')
        .length;
    }

    if (role === APP_CONSTANT.ROLE_LEGAL_GUARDIAN) {
      res.forEach((documentSection) => {
        num += documentSection.documents.filter(
          (item) => item.status === 'pending'
        ).length;
      });
    }

    setNumberDocument(num);
    setDataUserDocument(res);
  }, [res]);

  React.useEffect(() => {
    if (Array.isArray(res) && pathOr(undefined, ['userInfo', 'id'])(cookies)) {
      setRes((prev) =>
        prev.map((item) => {
          if (item.contactId === cookie.userInfo.id) {
            return {
              ...item,
              nameFirst: pathOr('', ['userInfo', 'name', 'nameFirst'])(cookies),
              nameLast: pathOr('', ['userInfo', 'name', 'nameLast'])(cookies)
            };
          }

          return item;
        })
      );
    } else {
      setRes((prev) => ({
        ...prev,
        nameFirst: pathOr('', ['userInfo', 'name', 'nameFirst'])(cookies),
        nameLast: pathOr('', ['userInfo', 'name', 'nameLast'])(cookies)
      }));
    }
  }, [cookies]);

  return (
    <LoadingContainer isLoading={isLoading}>
      <div className={classes.userBalanceWrapper}>
        <DocumentIcon />
        {isError ? (
          <p
            className={`${classes.messageError} ${classes.mb20} ${classes.mt15}`}
          >
            {t('documentAttentions.error')}
          </p>
        ) : (
          <>
            <p className={`${classes.message} ${classes.mb20} ${classes.mt15}`}>
              {t('documentAttentions', { count: numberDocument })}
            </p>

            {role === APP_CONSTANT.ROLE_LEGAL_GUARDIAN && numberDocument > 0 && (
              <>
                <TableWrapper>
                  <TableHead>
                    <TableRow>
                      <TableCellDocument align="left">
                        <p className={classes.tableHeader}>
                          {t('dashboard.appointment.table.patient')}
                        </p>
                      </TableCellDocument>
                      <TableCellDocument align="right">
                        <p className={classes.tableHeader}>
                          {t('dashboard.appointment.table.quantity')}
                        </p>
                      </TableCellDocument>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {(dataUserDocumentation as ILegalGuardianDocument[]).map(
                      (row, index) => {
                        const attentionDocs = row.documents.filter(
                          (item) => item.status === 'pending'
                        );

                        if (attentionDocs && attentionDocs.length > 0) {
                          return (
                            <TableRow key={index}>
                              <TableCellDocument align="left">
                                {row.nameFirst} {row.nameLast}
                              </TableCellDocument>
                              <TableCellDocument align="right">
                                {attentionDocs.length}
                              </TableCellDocument>
                            </TableRow>
                          );
                        }

                        return <></>;
                      }
                    )}
                  </TableBody>
                </TableWrapper>

                <TableWrapper>
                  <TableHead>
                    <TableRow>
                      <TableCellDocument align="left">
                        <SpanStyleBold>
                          {' '}
                          {t('dashboard.userBalance.total')}{' '}
                        </SpanStyleBold>
                      </TableCellDocument>
                      <TableCellDocument align="right">
                        <SpanStyleBold> {numberDocument} </SpanStyleBold>
                      </TableCellDocument>
                    </TableRow>
                  </TableHead>
                </TableWrapper>
              </>
            )}

            <PrimaryButton
              title={t('dashboard.appointment.button.document')}
              variant="contained"
              titleClassName="dashboard"
              isShowIcon={true}
              onClick={goToPatientDocumentList}
              style={{ width: '90%' }}
            />
          </>
        )}
      </div>
    </LoadingContainer>
  );
};

export default UserDocumentAttention;
